import React, { memo, useMemo, useRef, useState } from 'react';
import { Button, Col, Dropdown, Input, Row } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useResponsive } from '@hooks/useResponsive';
import type { TextModeType } from '@pages/MediaViewer/components/TranscriptDrawer/components/TranscriptionContent/TranscriptionContent';
import { ArrowDownIcon, CrossIcon, SearchIcon } from '@icons';

import './HeadPanel.less';

type HeadPanelProps = {
  setSearchValue: (value: string) => void;
  textMode: TextModeType;
  setTextMode: (value: TextModeType) => void;
  searchText: string;
  matchesCount: number;
  activeSearchMatchIndex: number;
  scrollToSearchMatch: (idx: number) => void;
};

function HeadPanel({
  setSearchValue,
  textMode,
  setTextMode,
  searchText,
  matchesCount,
  activeSearchMatchIndex,
  scrollToSearchMatch
}: HeadPanelProps) {
  const { isMdWidth } = useResponsive();
  const [isShowSearch, setIsShowSearch] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState('');
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();

  const textModeOptions = useMemo(() => {
    const items: ItemType[] = [
      {
        key: 'transcript',
        label: 'Transcript'
      },
      {
        key: 'captions',
        label: 'Captions'
      }
    ];
    if (isMdWidth) {
      items.push({
        key: 'cancel',
        label: 'Cancel'
      });
    }
    return items;
  }, [isMdWidth]);

  const textModeLabel = useMemo(() => {
    const option = textModeOptions.find((option) => option?.key === textMode);
    return option && 'label' in option ? option.label : 'Transcript';
  }, [textMode, textModeOptions]);

  const onSearchChange = (value: string) => {
    setLocalSearchValue(value);
    if (timeoutId.current) clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      setSearchValue(value);
    }, 600);
  };

  return (
    <div className="media_viewer_transcript-head-panel">
      {isShowSearch && (
        <div className="media_viewer_transcript-head-panel-search">
          <Input
            allowClear={{
              clearIcon: (
                <Row
                  onClick={(e) => e.stopPropagation()}
                  wrap={false}
                  gutter={8}
                  align="middle"
                >
                  {!!searchText && !!matchesCount && (
                    <>
                      <Col>
                        {activeSearchMatchIndex + 1}/{matchesCount}
                      </Col>
                      <Col>
                        <Row>
                          <Col
                            onClick={() =>
                              scrollToSearchMatch(activeSearchMatchIndex + 1)
                            }
                            className="media_viewer_transcript-head-panel-arrow"
                          >
                            <ArrowDownIcon />
                          </Col>
                          <Col
                            onClick={() =>
                              scrollToSearchMatch(activeSearchMatchIndex - 1)
                            }
                            className="media_viewer_transcript-head-panel-arrow"
                            style={{
                              transform: 'rotate(180deg)',
                              paddingTop: '2px',
                              alignItems: 'flex-start'
                            }}
                          >
                            <ArrowDownIcon />
                          </Col>
                        </Row>
                      </Col>
                    </>
                  )}
                  <Col>
                    <div className="b-line" />
                  </Col>
                  <Col>
                    <CrossIcon
                      style={{ fontSize: 24 }}
                      onClick={() => {
                        onSearchChange('');
                        setIsShowSearch(false);
                      }}
                    />
                  </Col>
                </Row>
              )
            }}
            value={localSearchValue}
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
            id="TRANSCRIPT_SEARCH_INPUT"
            autoFocus={true}
            prefix={<SearchIcon />}
            className="media_viewer_transcript-head-panel-search-input"
            placeholder="Search transcript"
          />
        </div>
      )}
      <Row wrap={false} justify="space-between" gutter={8}>
        <Col flex="auto">
          <Dropdown
            menu={{
              selectable: true,
              selectedKeys: [textMode],
              onClick: ({ key }: { key: string }) => {
                if (key.indexOf('cancel') < 0) {
                  setTextMode(key as 'captions' | 'transcript');
                }
              },
              items: textModeOptions
            }}
            prefixCls="media_viewer_menu"
            overlayClassName="media_viewer_mobile-overlay"
            placement="bottom"
            trigger={['click']}
          >
            <Button className="media_viewer_btn" style={{ width: '100%' }}>
              {textModeLabel}
              <ArrowDownIcon style={{ fontSize: 24 }} />
            </Button>
          </Dropdown>
        </Col>
        <Col>
          <Button
            htmlType="button"
            onClick={() => {
              setIsShowSearch(true);
            }}
            className="media_viewer_btn b-only-icon"
          >
            <SearchIcon style={{ fontSize: 16 }} />
          </Button>
        </Col>
      </Row>
    </div>
  );
}

export default memo(HeadPanel);
